<script setup lang="ts">
import DispenserIcon from '@/icons/dispenser.svg?component'
import ParcelIcon from '@/icons/parcel.svg?component'
import PercentIcon from '@/icons/percent.svg?component'
import TickIcon from '@/icons/tick.svg?component'
import BwrButton from './Button.vue'
import BwrRadio from './form-fields/Radio.vue'
import Select from './form-fields/Select.vue'
import IconPencil from './icons/IconPencil.vue'
import IconStatusError from './icons/IconStatusError.vue'
import IconStatusWarning from './icons/IconStatusWarning.vue'
import IconTick from './icons/IconTick.vue'
import BwrMessage from './Message.vue'
import PurchaseLoading from './PurchaseLoading.vue'

import type { Product } from '@/lib/types/Product'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { User } from '@/lib/types/User'
import { computed } from 'vue'
import formatMoney from '../lib/formatters/money'
import * as DateUtils from '../lib/utils/recharge-dates'

const props = withDefaults(
  defineProps<{
    price: number
    subscribePrice: number | null
    pricePer: {
      price: number
      unit: string
      subscribePrice: number | null
    } | null
    compareAtPrice: string | null
    allowSubscribe: boolean
    savePercent: number
    selectedSellingPlanId: string | null
    sellingPlanAllocations:
      | Product['variants'][number]['sellingPlanAllocations']
      | null
    quantity: number
    nextOrderDate: string | null
    when: 'now' | 'next'
    isLoggedIn: boolean
    loginLink: string
    userLoadState: string
    subscriptionToThisProduct: User['subscriptions'][number] | null
    customerPortalLink: string
    nextOrderLink: string
    purchaseState: string
    inTab: boolean
    showInlineVariantSelector: boolean
    variants: Product['variants']
    variantOptionLabel: string
    selectedVariantId: string
    available: boolean
    preSale?: boolean
    waitlistPageUrl?: string
    url: string
    immediateCheckout?: boolean
    hasDispensers: boolean
    isTrial: boolean
    freeDelivery: boolean
  }>(),
  { inTab: true },
)

const hasSubscriptions = computed(() => nextOrderDate.value !== null)

const nextOrderDate = computed(() => {
  if (props.nextOrderDate) {
    const date = DateUtils.parse(props.nextOrderDate)
    if (date) {
      return DateUtils.format(date)
    } else {
      return null
    }
  } else {
    return null
  }
})

const formattedNextOrderDate = computed(() =>
  nextOrderDate.value ? DateUtils.format(new Date(nextOrderDate.value)) : null,
)

const nextDeliveryOfThisProduct = computed(() => {
  if (
    props.subscriptionToThisProduct &&
    props.subscriptionToThisProduct.next_charge_scheduled_at
  ) {
    const date = DateUtils.parse(
      props.subscriptionToThisProduct.next_charge_scheduled_at,
    )
    return date ? DateUtils.format(date) : null
  } else {
    return null
  }
})

const subscribeAndSave = computed(
  () => props.subscribePrice !== null && props.price > props.subscribePrice,
)

const trackLoginNudge = () => {
  window.mixpanel.track('login_nudge_click')
}

const emit = defineEmits<{
  (e: 'when:changed', when: 'now' | 'next'): void
  (e: 'selectedSellingPlan:changed', sellingPlanId: string | null): void
  (e: 'viewProduct'): void
  (e: 'variant:changed', variantId: string): void
  (e: 'quantity:changed', quantity: number): void
  (e: 'purchase', purchaseOptions?: Partial<PurchaseData>): void
}>()

const onWhenChange = (when: 'now' | 'next') => {
  if (props.purchaseState !== 'IN_PROGRESS') emit('when:changed', when)
}

const changeSellingPlan = (event: 'once' | 'subscribe') => {
  if (props.sellingPlanAllocations && props.sellingPlanAllocations.length > 0) {
    if (event === 'subscribe') {
      emit(
        'selectedSellingPlan:changed',
        props.sellingPlanAllocations[0].sellingPlan.id.toString(),
      )
    } else {
      emit('selectedSellingPlan:changed', null)
    }
  }
}
</script>

<template>
  <div>
    <div class="Stack Stack--small">
      <div class="Purchase__PriceLine">
        <div class="Purchase__PriceLineLeft">
          <template v-if="subscribeAndSave && selectedSellingPlanId !== null">
            <span class="Purchase__Price" v-if="subscribePrice !== null">{{
              subscribePrice === 0 ? 'FREE' : formatMoney(subscribePrice)
            }}</span>
            <span v-if="subscribeAndSave" class="Purchase__PriceCompare">
              <s>{{ formatMoney(price) }}</s>
            </span>
            <span v-if="compareAtPrice" class="Purchase__PriceCompare">
              <s>{{ formatMoney(compareAtPrice) }}</s>
            </span>
          </template>
          <template v-else>
            <span class="Purchase__Price">{{ formatMoney(price) }}</span>
            <span v-if="compareAtPrice" class="Purchase__PriceCompare"
              ><s>{{ formatMoney(compareAtPrice) }}</s></span
            >
          </template>
        </div>
        <div class="Purchase__PriceLineRight">
          <template v-if="subscribeAndSave && selectedSellingPlanId !== null">
            <span v-if="pricePer" class="Purchase__PricePer"
              ><s v-if="subscribeAndSave">{{ formatMoney(pricePer.price) }}</s
              >&nbsp;
              <template v-if="pricePer.subscribePrice"
                >{{ formatMoney(pricePer.subscribePrice) }} /
                {{ pricePer.unit }}</template
              ></span
            >
          </template>
          <template v-else>
            <span v-if="pricePer" class="Purchase__PricePer"
              >{{ formatMoney(pricePer.price) }} / {{ pricePer.unit }}</span
            >
          </template>
        </div>
      </div>
      <template v-if="userLoadState === 'LOADING'">
        <purchase-loading />
      </template>
      <template v-else-if="userLoadState === 'ERROR'">
        <bwr-message mode="error">
          <template #icon>
            <icon-status-error />
          </template>
          <template #content>
            <p>
              Sorry, there was an error loading your options. Please refresh the
              page and try again.
            </p>
          </template>
        </bwr-message>
      </template>
      <template v-else-if="subscriptionToThisProduct">
        <div class="Stack Stack--small">
          <div
            v-if="showInlineVariantSelector && Object.keys(variants).length > 1"
            class="Purchase__Options"
          >
            <span v-if="variantOptionLabel" class="Purchase__OptionsLabel">
              Choose {{ variantOptionLabel.toLowerCase() }}
            </span>
            <div class="Purchase__Option Purchase__Option--wide">
              <Select
                :value="selectedVariantId"
                :disabled="purchaseState === 'IN_PROGRESS'"
                @change="$emit('variant:changed', $event)"
              >
                <option
                  v-for="variantId in Object.keys(variants)"
                  :key="variantId"
                  :value="variantId"
                >
                  {{ variants[variantId].title }} –
                  {{ formatMoney(variants[variantId].price) }}
                </option>
              </Select>
            </div>
          </div>
          <div class="Stack Stack--tiny">
            <p><strong>You’re already subscribed to this product</strong></p>
            <p>
              We'll send this in your order that dispatches on
              {{ nextDeliveryOfThisProduct }}
            </p>
          </div>
          <div class="Stack Stack--small">
            <bwr-button
              :href="`${customerPortalLink}?redirect_to=${encodeURIComponent(
                '/product-subscriptions',
              )}`"
              :modifiers="['xs']"
            >
              <span class="Button__Icon Button__Icon--before"
                ><icon-pencil />
              </span>
              Manage your subscription
            </bwr-button>
            <div v-if="!inTab" class="Stack Stack--small">
              <div class="Divider" />
              <p>Need a one-off delivery today?</p>
              <bwr-button
                :modifiers="['xs', 'third-width']"
                :loading="purchaseState === 'IN_PROGRESS'"
                :disabled="purchaseState === 'COMPLETE'"
                @click="
                  emit('purchase', {
                    quantity: 1,
                    when: 'now',
                  })
                "
              >
                <template v-if="purchaseState === 'COMPLETE'">
                  <icon-tick :animate="true" />
                  Added
                </template>
                <template v-else>Add to basket</template>
              </bwr-button>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="Stack Stack--small">
          <div
            v-if="showInlineVariantSelector && Object.keys(variants).length > 1"
            class="Purchase__Options"
          >
            <span v-if="variantOptionLabel" class="Purchase__OptionsLabel">
              Choose {{ variantOptionLabel.toLowerCase() }}
            </span>
            <div class="Purchase__Option Purchase__Option--wide">
              <Select
                :value="selectedVariantId"
                :disabled="purchaseState === 'IN_PROGRESS'"
                @change="$emit('variant:changed', $event)"
              >
                <option
                  v-for="variantId in Object.keys(variants)"
                  :key="variantId"
                  :value="variantId"
                >
                  {{ variants[variantId].title }} –
                  {{ formatMoney(variants[variantId].price) }}
                </option>
              </Select>
            </div>
          </div>
          <div>
            <Select
              :value="selectedSellingPlanId !== null ? 'subscribe' : 'once'"
              @change="changeSellingPlan"
            >
              <option value="subscribe">Subscribe</option>
              <option value="once">Once only</option>
            </Select>
          </div>
          <div class="Purchase__FeatureList" v-if="!isTrial">
            <p class="Purchase__FeatureListItem">
              <DispenserIcon />
              <span>
                {{ Math.round(savePercent) }}% off your first order{{
                  freeDelivery ? ' and FREE delivery!' : '!'
                }}
                Everything you need to get started{{
                  hasDispensers ? ', including dispensers' : ''
                }}.
              </span>
            </p>
            <p class="Purchase__FeatureListItem">
              <ParcelIcon />
              <span>
                {{
                  hasDispensers
                    ? 'Refills delivered when you need them. 20% off all future orders with Subscribe & Save'
                    : 'Delivered when you need them. 20% off all future orders with Subscribe & Save'
                }}
              </span>
            </p>
            <p class="Purchase__FeatureListItem">
              <TickIcon />
              <span>
                You're in total control. Edit frequency, swap products, delay or
                cancel at any time.
              </span>
            </p>
          </div>
          <div class="Purchase__FeatureList" v-else>
            <p class="Purchase__FeatureListItem">
              <ParcelIcon />
              <span>
                Start with 8 free washes. Just cover the £1 for postage.
              </span>
            </p>
            <p class="Purchase__FeatureListItem">
              <PercentIcon />
              <span>
                Two weeks after your trial, we'll send you our full-size 72
                tablet box with 20% off (that's only 22p per wash!)
              </span>
            </p>

            <p class="Purchase__FeatureListItem">
              <TickIcon />
              <span>
                You're in total control. Cancel, delay or modify your order at
                any time - no lock-in.
              </span>
            </p>
          </div>
        </div>
        <bwr-radio
          v-if="hasSubscriptions"
          name="when"
          :value="when"
          :options="[
            {
              label: `Add to next order (${formattedNextOrderDate})`,
              value: 'next',
              link: { text: 'View', href: nextOrderLink },
            },
            { label: 'Add to basket to send now', value: 'now' },
          ]"
          :disabled="purchaseState === 'IN_PROGRESS'"
          @change="onWhenChange"
        />
        <div class="Stack Stack--tiny">
          <template v-if="!available && preSale && waitlistPageUrl">
            <bwr-message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>
                  This product is coming soon! Join the waitlist to be the first
                  to know when it launches.
                </p>
              </template>
            </bwr-message>
            <bwr-button :href="waitlistPageUrl" :modifiers="['block']">
              Join the waitlist
            </bwr-button>
          </template>
          <template v-else-if="!available">
            <bwr-message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>This product is not currently available</p>
              </template>
            </bwr-message>
            <bwr-button
              id="BIS_trigger"
              :modifiers="['block']"
              :extra-attributes="{ 'data-variant-id': selectedVariantId }"
            >
              Notify me when back in stock
            </bwr-button>
          </template>
          <bwr-button
            v-else
            :modifiers="['block']"
            :loading="purchaseState === 'IN_PROGRESS'"
            :disabled="purchaseState === 'COMPLETE'"
            @click="emit('purchase')"
          >
            <template v-if="purchaseState === 'COMPLETE'" #iconBefore>
              <icon-tick :animate="true" />
            </template>
            <template v-if="purchaseState === 'COMPLETE'">Added</template>
            <template v-else-if="when === 'now' || !hasSubscriptions">
              <template v-if="immediateCheckout">Checkout now</template>
              <template v-else>
                Add
                {{ selectedSellingPlanId !== null ? 'subscription' : '' }} to
                basket
              </template>
            </template>
            <template v-else>
              Add {{ selectedSellingPlanId !== null ? 'subscription' : '' }} to
              order
            </template>
          </bwr-button>
          <template v-if="purchaseState === 'ERROR'">
            <bwr-message mode="error">
              <template #icon>
                <icon-status-error />
              </template>
              <template #content>
                <p>Sorry, there was an error. Please try again.</p>
              </template>
            </bwr-message>
          </template>
          <p
            v-if="!isLoggedIn && inTab"
            class="Purchase__LoginNudge"
            @click="trackLoginNudge"
          >
            Already a subscriber?
            <a class="Link" :href="loginLink">Log in</a> to manage your orders
          </p>
          <p
            v-if="!inTab"
            class="Purchase__ViewLink"
            @click="emit('viewProduct')"
          >
            or
            <a class="Link" :href="url">view product page</a> for more options
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
